import { Injectable } from '@angular/core';
import { gsap } from 'gsap';
import { registerRsPlugins, rsFromTo, RsMotionService } from '../motion';

/**
 * Hero choreography for the shared service banner.
 *
 * One `gsap.context()` scoped to the banner root, torn down by a single
 * `revert()` from `ngOnDestroy` — no ScrollTrigger or listener survives the
 * component. SSR-safe, honours `prefers-reduced-motion`, and animates only
 * `transform` / `opacity`.
 *
 * Layering rule used throughout: GSAP owns the `transform` channel, cursor
 * parallax rides on the separate `translate` property. The two therefore compose
 * instead of overwriting each other, which is what lets the mockups float and
 * track the pointer at the same time.
 */
@Injectable()
export class BannerMotionService {
  private ctx?: gsap.Context;
  private cleanups: Array<() => void> = [];

  constructor(private motion: RsMotionService) {}

  init(root: HTMLElement): void {
    if (!this.motion.enabled) return;

    this.motion.arm();
    registerRsPlugins();

    this.ctx = gsap.context(() => {
      this.buildEntrance();
      this.buildFloats();
      // the consultation card owns its own reveal (Angular Animations)
      this.buildScrollScale(root);
    }, root);

    const hero = root.querySelector<HTMLElement>('.rsb-hero');
    if (hero) {
      this.cleanups.push(this.motion.trackPointer(hero), this.motion.pauseOffscreen(hero));
    }
  }

  /* -------------------------------------------------------------- entrance -- */

  private buildEntrance(): void {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo('.rsb-hero__eyebrow', ...rsFromTo({ opacity: 0, y: 18, duration: 0.55 }))
      .fromTo(
        '.rsb-hero__title', ...rsFromTo({
          opacity: 0,
          y: 60,
          duration: 1,
          onStart: () => this.hold('.rsb-hero__title'),
          onComplete: () => this.release('.rsb-hero__title'),
        }),
        '-=0.28'
      )
      .fromTo('.rsb-hero__lede', ...rsFromTo({ opacity: 0, y: 24, duration: 0.7 }), '-=0.62')
      .fromTo('.rsb-hero__cta > *', ...rsFromTo({ opacity: 0, y: 20, duration: 0.6, stagger: 0.15 }), '-=0.45')
      .fromTo('.rsb-pill', ...rsFromTo({ opacity: 0, y: 22, duration: 0.6, stagger: 0.09 }), '-=0.4');

    // device stack rises, unrotates and scales up
    tl.fromTo(
      '.rsb-stage', ...rsFromTo({
        opacity: 0,
        scale: 0.9,
        rotate: -5,
        duration: 1.1,
        onStart: () => this.hold('.rsb-stage'),
        onComplete: () => this.release('.rsb-stage'),
      }),
      '-=1.5'
    )
      .fromTo(
        '.rsb-float', ...rsFromTo({ opacity: 0, scale: 0.72, duration: 0.6, stagger: 0.08, ease: 'back.out(1.6)' }),
        '-=0.55'
      )
      .fromTo('.rsb-stats', ...rsFromTo({ opacity: 0, x: 34, duration: 0.7 }), '-=0.5')
      .fromTo('.rsb-stat', ...rsFromTo({ opacity: 0, y: 18, duration: 0.5, stagger: 0.09 }), '-=0.45');
  }

  /**
   * Continuous float on the mockups and the stat card — 6px on the monitor,
   * 10px on the phone, per the reference. Runs on the transform channel so it
   * never fights the pointer parallax, which uses `translate`.
   */
  private buildFloats(): void {
    const float = (selector: string, amount: number, duration: number) => {
      gsap.utils.toArray<HTMLElement>(selector).forEach((el, i) => {
        gsap.to(el, {
          y: -amount,
          duration,
          delay: i * 0.2,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        });
      });
    };

    float('.rsb-stage__monitor', 6, 4.2);
    float('.rsb-stage__phone', 10, 3.4);
    float('.rsb-stats', 8, 4.6);
  }

  /* ------------------------------------------------------------ scroll feel -- */

  /** The hero settles back to 0.96 as it leaves — subtle depth, no pinning. */
  private buildScrollScale(root: HTMLElement): void {
    const hero = root.querySelector<HTMLElement>('.rsb-hero__inner');
    if (!hero) return;

    gsap.to(hero, {
      scale: 0.96,
      ease: 'none',
      scrollTrigger: {
        trigger: root,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  }

  /* --------------------------------------------------------------- helpers -- */

  private hold(selector: string): void {
    gsap.utils
      .toArray<HTMLElement>(selector)
      .forEach((el) => (el.style.willChange = 'transform, opacity'));
  }

  private release(selector: string): void {
    gsap.utils.toArray<HTMLElement>(selector).forEach((el) => (el.style.willChange = 'auto'));
  }

  destroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.ctx?.revert();
    this.ctx = undefined;
  }
}
